import React, { useEffect, useState } from "react";
import { Label, Tooltip } from "@vibe/core";
import { ExternalPage, Calendar, DropdownChevronDown } from "@vibe/icons";
import { generateItemUrl } from "../../utils/mondayLinks";
import { formatDate } from "../../utils/formatDate";
import logger from "../../lib/logger";
import { getBoardUsageDetails } from "../../services/monday-service";
import { MondayColors } from "@data/mondayColors";
import { Skeleton } from "@vibe/core";

interface UsageItem {
  itemId: string | number;
  created_at: string;
}

interface BoardItemData {
  id: string | number;
  name: string;
}

interface BoardData {
  state?: string;
  name?: string;
}

interface BoardUsageDetails {
  boardData?: BoardData[];
  itemData?: BoardItemData[];
}

interface UsageBoardCardProps {
  boardId: string | number;
  boardName: string;
  index: number;
  isExpanded: boolean;
  items: UsageItem[];
  onToggle: (boardId: string | number) => void;
  slug: string;
}

const getItemName = (itemData: BoardItemData[], itemId: string | number) => {
  const matchedItem = itemData.find((data) => data.id.toString() === itemId.toString());

  return matchedItem?.name ?? itemId.toString();
};

const UsageBoardCard = ({
  boardId,
  index,
  isExpanded,
  items,
  onToggle,
  slug,
}: UsageBoardCardProps) => {
  const [itemsData, setItemsData] = useState<BoardItemData[]>([]);
  const [boardName, setBoardName] = useState("");
  const [boardState, setBoardState] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const boardColor = MondayColors[index % MondayColors.length];
  const itemLabel = items.length === 1 ? "item" : "items";

  useEffect(() => {
    const fetchBoardDetails = async () => {
      try {
        setIsLoading(true);
        const response = (await getBoardUsageDetails(boardId, items)) as
          | BoardUsageDetails
          | undefined;

        setBoardName(response?.boardData?.[0]?.name ?? "Private Board");
        setBoardState(response?.boardData?.[0]?.state ?? "");
        setItemsData(response?.itemData ?? []);
      } catch (error) {
        logger.error("Error fetching usage data", { error });
      } finally {
        setIsLoading(false);
      }
    };

    fetchBoardDetails();
  }, [boardId, items]);

  return (
    <div className="border-t-0 border-b border-gray-200 border-solid cursor-pointer border-x-0 last:border-b-0">
      <div
        role="button"
        onClick={() => onToggle(boardId)}
        className="flex justify-between items-center px-4 py-3 w-full bg-transparent border-0 transition-colors cursor-pointer hover:bg-gray-50"
      >
        <div className="flex gap-4 items-center">
          <div
            data-testid="board-color-bar"
            className="w-1 h-12 rounded-full"
            style={{ backgroundColor: boardColor }}
          />
          <div className="flex flex-col gap-1 text-left">
            <div className="flex gap-3 items-center">
              {isLoading ? (
                <Skeleton type="text" size="h4" width={100} />
              ) : (
                <h4 className="font-normal text-gray-900">{boardName}</h4>
              )}
              {(boardState === "deleted" || boardState === "archived") && (
                <Label
                  text={boardState.toUpperCase()}
                  color={boardState === "archived" ? "dark" : "negative"}
                  labelClassName="label-inner-text-wrapper"
                />
              )}
            </div>
            <p className="text-sm text-gray-500 mt-0.5">
              {items.length} connected {itemLabel}
            </p>
          </div>
        </div>
        <div className="flex gap-4 items-center">
          <DropdownChevronDown
            size={16}
            color={"#676879"}
            className={` transition-transform ${isExpanded ? "rotate-180" : ""}`}
          />
        </div>
      </div>

      {isExpanded && (
        <div className="bg-gray-50 border-t border-b-0 border-gray-200 border-solid border-x-0">
          <div className="px-4 py-3">
            <div className="space-y-2">
              {items.map((item) => (
                <div
                  key={item.itemId}
                  className="flex justify-between items-center px-4 py-3 bg-white rounded-lg border border-gray-200 border-solid cursor-pointer"
                  onClick={() => {
                    const url = generateItemUrl(slug, boardId, item.itemId);
                    window.open(url, "_blank", "noopener,noreferrer");
                  }}
                >
                  {isLoading ? (
                    <Skeleton type="text" size="p" width={100} />
                  ) : (
                    <div className="flex gap-3 items-center min-w-0">
                      <span className="flex-shrink-0">
                        <ExternalPage size={16} color={"#676879"} />
                      </span>
                      <Tooltip zIndex={1210} content={getItemName(itemsData, item.itemId)}>
                        <span className="font-medium text-gray-900 no-underline truncate cursor-pointer">
                          {getItemName(itemsData, item.itemId)}
                        </span>
                      </Tooltip>
                    </div>
                  )}
                  <div className="flex gap-2 items-center text-sm text-gray-500">
                    <Calendar size={16} color={"#676879"} />
                    {formatDate(item.created_at)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsageBoardCard;
